/**
 * Global Type Guards
 *
 * Type guards are functions that narrow types during runtime checks.
 * They can be reused across the application to ensure type safety.
 *
 * @example
 * ```typescript
 * // Instead of: const value = stringValue as MyType
 * if (isMyType(stringValue)) {
 *   // TypeScript knows stringValue is MyType here
 * }
 * ```
 */

/**
 * Checks if a value is a string and not empty
 */
export function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

/**
 * Checks if a value is a valid number (not NaN, Infinity, or -Infinity)
 */
export function isValidNumber(value: unknown): value is number {
  return typeof value === "number" && !isNaN(value) && isFinite(value);
}

/**
 * Checks if a value is a valid object (not null and not array)
 */
export function isPlainObject(
  value: unknown
): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Checks if a value is an array
 */
export function isArray<T>(value: unknown): value is T[] {
  return Array.isArray(value);
}

/**
 * Checks if a value is an Error object
 */
export function isError(value: unknown): value is Error {
  return value instanceof Error;
}

/**
 * Checks if a value has a specific property
 */
export function hasProperty<T extends string>(
  obj: unknown,
  prop: T
): obj is Record<T, unknown> {
  return isPlainObject(obj) && prop in obj;
}

/**
 * Type guard for Firebase User
 * Checks if a value is a Firebase User object with required properties
 */
export function isFirebaseUser(value: unknown): value is {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  providerData: Array<{ providerId: string }>;
} {
  return (
    isPlainObject(value) &&
    "uid" in value &&
    typeof value.uid === "string" &&
    "email" in value &&
    (value.email === null || typeof value.email === "string") &&
    "emailVerified" in value &&
    typeof value.emailVerified === "boolean" &&
    "providerData" in value &&
    Array.isArray(value.providerData)
  );
}

/**
 * Type guard for pagination parameters
 */
export function isPaginationParams(value: unknown): value is {
  page: number;
  pageSize: number;
} {
  return (
    isPlainObject(value) &&
    "page" in value &&
    typeof value.page === "number" &&
    value.page >= 0 &&
    "pageSize" in value &&
    typeof value.pageSize === "number" &&
    value.pageSize > 0
  );
}

/**
 * Type guard for API error response
 */
export function isApiError(value: unknown): value is {
  error: string;
  message: string;
  statusCode: number;
} {
  return (
    isPlainObject(value) &&
    "error" in value &&
    typeof value.error === "string" &&
    "message" in value &&
    typeof value.message === "string"
  );
}

/**
 * Type guard for URL string
 */
export function isUrl(value: unknown): value is string {
  if (typeof value !== "string") {
    return false;
  }
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Type guard for Email string (basic validation)
 */
export function isEmail(value: unknown): value is string {
  if (typeof value !== "string") {
    return false;
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value);
}

/**
 * Type guard for UUID string
 */
export function isUuid(value: unknown): value is string {
  if (typeof value !== "string") {
    return false;
  }
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
}

/**
 * Type guard for Record ID
 * Alias for UUID validation
 */
export function isRecordId(value: unknown): value is string {
  return isUuid(value);
}

/**
 * Type guard for timestamps (ISO 8601 string or number)
 */
export function isTimestamp(value: unknown): value is string | number {
  if (typeof value === "number") {
    return value > 0;
  }
  if (typeof value === "string") {
    return !isNaN(Date.parse(value));
  }
  return false;
}

/**
 * Type guard for boolean-like values (string "true"/"false" or actual boolean)
 */
export function isBooleanLike(value: unknown): value is boolean | string {
  return (
    typeof value === "boolean" ||
    (typeof value === "string" && (value === "true" || value === "false"))
  );
}

/**
 * Type guard for date range
 */
export function isDateRange(value: unknown): value is {
  from: Date | string;
  to: Date | string;
} {
  return (
    isPlainObject(value) &&
    "from" in value &&
    "to" in value &&
    (value.from instanceof Date ||
      typeof value.from === "string" ||
      typeof value.from === "number") &&
    (value.to instanceof Date ||
      typeof value.to === "string" ||
      typeof value.to === "number")
  );
}

